import { type ReactNode } from 'react';

export interface QueryBoundaryProps<T> {
  isPending: boolean;
  isError: boolean;
  error?: Error | null;
  data: T | undefined;
  isEmpty?: boolean;
  children: ReactNode | ((data: NonNullable<T>) => ReactNode);
  loading?: ReactNode;
  errorFallback?: ReactNode | ((error: Error, retry: () => void) => ReactNode);
  empty?: ReactNode;
}

export function QueryBoundary<T>({
  isPending,
  isError,
  error,
  data,
  isEmpty,
  children,
  loading,
  errorFallback,
  empty,
}: QueryBoundaryProps<T>): ReactNode {
  if (isPending) {
    return loading ?? (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--border-subtle)] border-t-brand-500" />
          <div className="text-sm text-[var(--text-secondary)]">Loading…</div>
        </div>
      </div>
    );
  }

  if (isError) {
    if (typeof errorFallback === 'function') {
      return error ? errorFallback(error, () => window.location.reload()) : null;
    }
    return errorFallback ?? (
      <div className="flex min-h-[200px] items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-coral-100">
            <svg className="h-5 w-5 text-coral-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
            </svg>
          </div>
          <div className="text-sm font-semibold text-ink-900 mb-1">Failed to load</div>
          <div className="text-xs text-ink-500 mb-3">Something went wrong. Please try again.</div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center rounded-lg bg-brand-500 px-3.5 py-2 text-xs font-semibold text-white shadow transition hover:bg-brand-600"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (isEmpty || (Array.isArray(data) && data.length === 0)) {
    return empty ?? (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="text-sm text-ink-500">No data found.</div>
      </div>
    );
  }

  if (typeof children === 'function') {
    return children(data as NonNullable<T>);
  }

  return children;
}
